// Product Grid Component
import { I18nManager } from "../../services/i18n-manager";
import { ProductService } from "../../services/product-service";
import { CartManager } from "../../state/cart-manager";
import { DialogManager } from "../dialogs/dialog-manager";

export interface Product {
  id: string;
  category_id: string;
  name: Record<string, string>;
  description: Record<string, string>;
  price_sek: number;
  images: string[];
  options: Record<string, any>;
  active: boolean;
  created_at: number;
}

export class ProductGrid {
  private productService: ProductService;
  private cartManager: CartManager;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private products: Product[] = [];
  private filteredProducts: Product[] = [];
  private currentCategory: string = "all";
  private isLoading: boolean = false;

  constructor(
    productService: ProductService,
    cartManager: CartManager,
    i18nManager: I18nManager,
    dialogManager: DialogManager
  ) {
    this.productService = productService;
    this.cartManager = cartManager;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
  }

  init(): void {
    this.setupEventListeners();
    this.loadProducts();
  }

  private setupEventListeners(): void {
    // Listen for language changes
    this.i18nManager.onLanguageChange(() => {
      this.render();
    });

    // Listen for category changes
    document.addEventListener("category-changed", (e: any) => {
      this.currentCategory = e.detail.categorySlug;
      this.filterProducts();
    });

    // Listen for cart changes
    this.cartManager.onCartUpdated(() => {
      this.updateAddToCartButtons();
    });
  }

  async loadProducts(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {
      this.products = await this.productService.loadProducts();
      this.filterProducts();
    } catch (error) {
      console.error("Failed to load products:", error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  private filterProducts(): void {
    if (this.currentCategory === "all") {
      this.filteredProducts = this.products.filter((product) => product.active);
    } else {
      this.filteredProducts = this.products.filter(
        (product) =>
          product.active && product.category_id === this.currentCategory
      );
    }
    this.render();
  }

  private render(): void {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    if (this.filteredProducts.length === 0) {
      this.showEmptyState();
      return;
    }

    const productCards = this.filteredProducts
      .map((product) => this.createProductCard(product))
      .join("");

    productGrid.innerHTML = productCards;
    this.setupProductCardEvents();
  }

  private createProductCard(product: Product): string {
    const name = this.productService.getLocalizedName(product);
    const description = this.productService.getLocalizedDescription(product);
    const price = this.i18nManager.formatCurrency(product.price_sek);
    const imageUrl =
      product.images.length > 0
        ? this.productService.getProductImageUrl(product.images[0])
        : this.productService.getPlaceholderImage();

    return `
      <div class="product-card" data-product-id="${product.id}">
        <div class="product-image">
          <img src="${imageUrl}" alt="${name}" loading="lazy">
          <div class="product-overlay">
            <button class="product-view-btn glass-button" data-product-id="${
              product.id
            }">
              ${this.i18nManager.t("products.view_details")}
            </button>
          </div>
        </div>
        
        <div class="product-info">
          <h3 class="product-name">${name}</h3>
          <p class="product-description">${description}</p>
          <div class="product-price">${price}</div>
          
          <div class="product-actions">
            <button class="product-add-btn glass-button glass-button--primary" data-product-id="${
              product.id
            }">
              ${this.i18nManager.t("products.add_to_cart")}
            </button>
            <button class="product-view-btn glass-button glass-button--secondary" data-product-id="${
              product.id
            }">
              ${this.i18nManager.t("products.view_details")}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private setupProductCardEvents(): void {
    // View details buttons
    document.querySelectorAll(".product-view-btn").forEach((button) => {
      button.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const productId = target.getAttribute("data-product-id");
        if (productId) {
          this.openProductDialog(productId);
        }
      });
    });

    // Add to cart buttons
    document.querySelectorAll(".product-add-btn").forEach((button) => {
      button.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const productId = target.getAttribute("data-product-id");
        if (productId) {
          this.addToCart(productId);
        }
      });
    });
  }

  private async openProductDialog(productId: string): Promise<void> {
    try {
      // Open the product dialog with the product ID
      this.dialogManager.openDialog("product-dialog", { productId });
    } catch (error) {
      console.error("Error opening product dialog:", error);
    }
  }

  private addToCart(productId: string): void {
    const product = this.products.find((p) => p.id === productId);
    if (!product) return;

    // Check if product has options/attributes that need to be selected
    const hasOptions = product.options && Object.keys(product.options).length > 0;
    
    if (hasOptions) {
      // Product has options - open dialog so user can select them
      this.openProductDialog(productId);
      // Show notification explaining why dialog opened
      this.dialogManager.showNotification(
        this.i18nManager.t("products.select_options_required") ||
          "Please select product options before adding to cart",
        "info"
      );
      return;
    }

    // No options - add directly to cart
    const cartItem = this.productService.createCartItem(product);
    this.cartManager.addItem(
      cartItem.productId,
      cartItem.name,
      cartItem.price,
      cartItem.quantity,
      cartItem.options,
      cartItem.image
    );

    this.showAddToCartSuccess();
  }

  private showAddToCartSuccess(): void {
    // Create success notification
    const notification = document.createElement("div");
    notification.className = "notification notification--success";
    notification.textContent = this.i18nManager.t("success.item_added_to_cart");

    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.classList.add("show");
    });

    setTimeout(() => {
      notification.classList.remove("show");
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  private updateAddToCartButtons(): void {
    // Update button states based on cart contents
    document.querySelectorAll(".product-add-btn").forEach((button) => {
      const productId = button.getAttribute("data-product-id");
      if (productId) {
        const cartItem = this.cartManager
          .getCart()
          .items.find((item) => item.productId === productId);

        if (cartItem) {
          button.textContent = this.i18nManager.t("products.in_cart");
          button.classList.add("in-cart");
        } else {
          button.textContent = this.i18nManager.t("products.add_to_cart");
          button.classList.remove("in-cart");
        }
      }
    });
  }

  private showLoading(): void {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    productGrid.innerHTML = `
      <div class="product-loading">
        <div class="loading-spinner"></div>
        <p>${this.i18nManager.t("products.loading")}</p>
      </div>
    `;
  }

  private showEmptyState(): void {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    productGrid.innerHTML = `
      <div class="product-empty">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6"/>
          </svg>
        </div>
        <h3>${this.i18nManager.t("products.not_found")}</h3>
        <p>${this.i18nManager.t("products.try_different_category")}</p>
      </div>
    `;
  }

  private showError(): void {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    productGrid.innerHTML = `
      <div class="product-error">
        <div class="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h3>${this.i18nManager.t("error.load_products")}</h3>
        <button class="glass-button" onclick="location.reload()">
          ${this.i18nManager.t("common.retry")}
        </button>
      </div>
    `;
  }

  // Public methods
  public getProducts(): Product[] {
    return this.products;
  }

  public getFilteredProducts(): Product[] {
    return this.filteredProducts;
  }

  public refresh(): void {
    this.loadProducts();
  }

  public filterByCategory(categorySlug: string): void {
    this.currentCategory = categorySlug;
    this.filterProducts();
  }
}
